// MARK://dhk8/pos/lib/offline-queue
// Client-side outbox for write operations (PORT-FROM-SC-RESTAURANT §2 rule 6).
// When the device is offline (or a write fails with a network error), the mutation
// is parked in IndexedDB with an idempotency key and replayed FIFO when connectivity
// returns. The server deduplicates by idempotency key — replays are safe.
//
// Scope: this is intentionally generic (method/url/body/idempotencyKey). Stores that
// want offline resilience call `enqueue()` on network failure and `flush()` on
// reconnect; the actual HTTP is done by the caller's `send` fn so auth/headers stay
// in one place (lib/api.ts).
//
// Persistence: IndexedDB (survives reload/crash). Falls back to an in-memory queue
// when IndexedDB is unavailable (SSR / private mode) so the API still works.

const DB_NAME = 'dhk8-pos'
const STORE = 'outbox'
const DB_VERSION = 1

/** A graph mutation parked for offline replay. The write path migrated REST->GraphQL
 *  (F8.1 / ADR-0009): the outbox must replay the SAME graph mutation the online path
 *  uses, not a dead REST descriptor. `name` selects the mutation; `vars` are the typed
 *  variables already shaped for that mutation (see lib/clients/orders.ts replay map).
 *  `idempotencyKey` rides along on the entry for when server idempotency (F9.6) lands. */
export interface OutboxGraphOp {
  name: 'addOrderItem' | 'updateOrderItem' | 'removeOrderItem' | 'fireOrder'
  orderId: string
  /** Mutation-specific variables (e.g. { input } for addOrderItem, { itemId, input }
   *  for updateOrderItem). Serialisable - survives an IndexedDB round-trip. */
  vars: Record<string, unknown>
}

export interface OutboxEntry {
  /** Monotonic-ish id; also the IndexedDB key. */
  id: string
  /** Idempotency key sent to the server (header `Idempotency-Key`). */
  idempotencyKey: string
  /** Logical operation name, for diagnostics / UI ("agregar producto", ...). */
  kind: string
  /** epoch ms when enqueued. */
  queuedAt: number
  /** number of failed replay attempts. */
  attempts: number
  /** Graph-mutation descriptor (preferred path - order writes are GraphQL now).
   *  When present, the caller's `send` replays it via gqlClient.mutation. */
  op?: OutboxGraphOp
  /** Legacy REST descriptor - still used by callers that haven't migrated off REST
   *  (e.g. the money-path payments, parked on REST by design). Mutually exclusive
   *  with `op` in practice; `send` replays it via apiFetch. */
  method?: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url?: string
  body?: unknown
}

export function newIdempotencyKey(): string {
  const c = typeof globalThis !== 'undefined' ? (globalThis as { crypto?: Crypto }).crypto : undefined
  if (c?.randomUUID) return c.randomUUID()
  const b = new Array(16)
  for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = b.map((x: number) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

// ── backing store: IndexedDB with an in-memory fallback ─────────────────────

interface Backing {
  add(e: OutboxEntry): Promise<void>
  all(): Promise<OutboxEntry[]>
  remove(id: string): Promise<void>
  update(e: OutboxEntry): Promise<void>
}

class MemoryBacking implements Backing {
  private map = new Map<string, OutboxEntry>()
  async add(e: OutboxEntry) { this.map.set(e.id, e) }
  async all() { return [...this.map.values()].sort((a, b) => a.queuedAt - b.queuedAt) }
  async remove(id: string) { this.map.delete(id) }
  async update(e: OutboxEntry) { this.map.set(e.id, e) }
}

class IdbBacking implements Backing {
  private dbp: Promise<IDBDatabase>
  constructor() {
    this.dbp = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' })
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }
  private tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    return this.dbp.then((db) => db.transaction(STORE, mode).objectStore(STORE))
  }
  private run<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  }
  async add(e: OutboxEntry) { await this.run((await this.tx('readwrite')).put(e)) }
  async update(e: OutboxEntry) { await this.run((await this.tx('readwrite')).put(e)) }
  async remove(id: string) { await this.run((await this.tx('readwrite')).delete(id)) }
  async all() {
    const items = await this.run<OutboxEntry[]>((await this.tx('readonly')).getAll() as IDBRequest<OutboxEntry[]>)
    return items.sort((a, b) => a.queuedAt - b.queuedAt)
  }
}

function pickBacking(): Backing {
  try {
    if (typeof indexedDB !== 'undefined') return new IdbBacking()
  } catch { /* fall through */ }
  return new MemoryBacking()
}

let backing: Backing | null = null
function store(): Backing {
  if (!backing) backing = pickBacking()
  return backing
}

/** Test seam — replace the backing store. */
export function __setOfflineBacking(b: Backing | null): void {
  backing = b
}

// ── public API ──────────────────────────────────────────────────────────────

type Listener = (depth: number) => void
const listeners = new Set<Listener>()
function notify(depth: number) { for (const l of listeners) l(depth) }

/** Subscribe to queue-depth changes (for a "N pendientes" badge). Returns an unsubscribe. */
export function onOutboxChange(l: Listener): () => void {
  listeners.add(l)
  void depth().then(l)
  return () => listeners.delete(l)
}

export async function depth(): Promise<number> {
  return (await store().all()).length
}

export interface EnqueueInput {
  kind: string
  idempotencyKey?: string
  /** Graph-mutation descriptor (preferred - order writes are GraphQL). */
  op?: OutboxGraphOp
  /** Legacy REST descriptor (payments etc. still on REST). */
  method?: OutboxEntry['method']
  url?: string
  body?: unknown
}

export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    id: newIdempotencyKey(),
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey(),
    kind: input.kind,
    queuedAt: Date.now(),
    attempts: 0,
    ...(input.op ? { op: input.op } : {}),
    ...(input.method ? { method: input.method } : {}),
    ...(input.url ? { url: input.url } : {}),
    ...(input.body !== undefined ? { body: input.body } : {}),
  }
  await store().add(entry)
  notify(await depth())
  return entry
}

export type SendFn = (entry: OutboxEntry) => Promise<void>

/** Replay queued entries FIFO. Stops at the first failure (keeps order) and bumps
 *  its attempt counter. Returns { sent, remaining }. Safe to call repeatedly. */
export async function flush(send: SendFn): Promise<{ sent: number; remaining: number }> {
  const entries = await store().all()
  let sent = 0
  for (const e of entries) {
    try {
      await send(e)
      await store().remove(e.id)
      sent++
      notify(await depth())
    } catch {
      await store().update({ ...e, attempts: e.attempts + 1 })
      break // preserve ordering — retry later
    }
  }
  const remaining = await depth()
  return { sent, remaining }
}

/** Clear the whole outbox (e.g. operator chose to discard unsynced work). */
export async function clear(): Promise<void> {
  for (const e of await store().all()) await store().remove(e.id)
  notify(0)
}
